import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AddressAutocomplete from '../components/AddressAutocomplete/AddressAutocomplete';
import Turnstile, { turnstileEnabled } from '../components/Turnstile';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function Signup({ lockedAccountType = null }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn, getToken, refreshSupplierInfo } = useAuth();

  // Two claim entry points:
  //  - claim=<token> (current): opaque token resolved server-side, email locked.
  //  - email=&account_type=Supplier (legacy links still in inboxes): prefill only.
  const claimToken = searchParams.get('claim') || '';
  const legacyClaimEmail = searchParams.get('email') || '';
  const legacyIsSupplierClaim =
    !!legacyClaimEmail && searchParams.get('account_type') === 'Supplier';

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(legacyClaimEmail);
  const [claimEmail, setClaimEmail] = useState(legacyClaimEmail); // canonical claim address
  const [emailLocked, setEmailLocked] = useState(false);          // true once a token resolves
  const [fullName, setFullName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountType, setAccountType] = useState(lockedAccountType || '');
  const [phone, setPhone] = useState('');
  const [warehouseAddress, setWarehouseAddress] = useState('');
  const [warehouseLat, setWarehouseLat] = useState(null);
  const [warehouseLng, setWarehouseLng] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  // Bumped after a failed signup to force a fresh Turnstile token (single-use).
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const isSupplierClaim = legacyIsSupplierClaim || emailLocked;
  const emailChanged =
    isSupplierClaim && !emailLocked &&
    email.trim().toLowerCase() !== claimEmail.trim().toLowerCase();

  // Resolve the claim token (idempotent GET — does not consume it) to prefill +
  // lock the email and prefill the business name. Invalid/expired tokens fall
  // through to a normal signup rather than blocking.
  useEffect(() => {
    if (!claimToken) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/auth/claim/${claimToken}`);
        if (!active) return;
        const data = res.data?.data;
        if (data?.email) {
          setEmail(data.email);
          setClaimEmail(data.email);
          setEmailLocked(true);
          if (data.supplierName) setBusinessName((prev) => prev || data.supplierName);
        }
      } catch (err) {
        // Invalid / expired / already-used token — silently allow normal signup.
      }
    })();
    return () => { active = false; };
  }, [claimToken]);

  // For enterprise (locked type), we skip the type-selection step.
  // Steps: 1 = details, 2 = type (skipped if locked), 3 = phone.
  const totalSteps = lockedAccountType ? 2 : 3;

  const validatePassword = (pw) => {
    if (pw.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(pw)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one number';
    return null;
  };

  const getPasswordStrength = () => {
    if (!password) return { label: '', color: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    if (strength <= 2) return { label: 'Weak', color: '#ef4444' };
    if (strength === 3) return { label: 'Fair', color: '#f59e0b' };
    if (strength === 4) return { label: 'Good', color: '#0ea5e9' };
    return { label: 'Strong', color: '#10b981' };
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim()) return setError('Please enter your full name');
    if (!businessName.trim()) return setError('Please enter your business name');
    if (!email.trim()) return setError('Please enter your email address');
    if (!password) return setError('Please enter a password');
    if (password !== confirmPassword) return setError('Passwords do not match');
    const pwErr = validatePassword(password);
    if (pwErr) return setError(pwErr);
    if (turnstileEnabled && !captchaToken) {
      return setError('Please complete the verification below.');
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, {
        email: email.trim(),
        full_name: fullName.trim(),
        password,
        phone: '',
        company_name: businessName.trim(),
        // Empty string -> server stores null. User can pick later.
        account_type: lockedAccountType || '',
        captcha_token: captchaToken,
        // Names the exact shadow supplier to claim (digest links). Harmless if absent.
        claim_token: claimToken || undefined,
      });
      if (response.data.success) {
        await signIn(email.trim(), password);
        setStep(2);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(
        errorMessage && errorMessage.includes('already registered')
          ? errorMessage
          : 'Failed to create account. Please try again in a moment.'
      );
      // The previous token is now spent; force a fresh challenge for the retry.
      setCaptchaToken('');
      setCaptchaResetKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = async (type) => {
    setError('');
    setLoading(true);
    setAccountType(type);
    try {
      const token = await getToken();
      await axios.patch(
        `${API_BASE_URL}/suppliers/profile`,
        { account_type: type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (refreshSupplierInfo) await refreshSupplierInfo();
    } catch (err) {
      console.error('Account type update failed:', err);
      // Account already exists; let the user proceed — they'll be re-prompted later.
    } finally {
      setLoading(false);
      setStep(3);
    }
  };

  const finish = () => {
    // Enterprise users skip self-serve billing; users without a type go home
    // (they'll be re-prompted for the type on next entry, then gated until they subscribe).
    if (!accountType || accountType === 'Enterprise') {
      navigate('/');
      return;
    }
    // Supplier/Distributor land on the billing tab to choose a plan
    // (Starter/Pro, monthly/annual) and start their 7-day trial.
    navigate('/billing');
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    const trimmedPhone = phone.trim();
    const trimmedWarehouse = warehouseAddress.trim();
    if (!trimmedPhone && !trimmedWarehouse) {
      finish();
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      const payload = {};
      if (trimmedPhone) payload.phone_1 = trimmedPhone;
      if (trimmedWarehouse) {
        const entry = { type: 'warehouse', raw: trimmedWarehouse };
        if (warehouseLat != null && warehouseLng != null) {
          entry.lat = warehouseLat;
          entry.lng = warehouseLng;
        }
        payload.addresses = [entry];
      }
      await axios.patch(
        `${API_BASE_URL}/suppliers/profile`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (refreshSupplierInfo) await refreshSupplierInfo();
    } catch (err) {
      console.error('Profile update failed:', err);
    } finally {
      setLoading(false);
      finish();
    }
  };

  const passwordStrength = getPasswordStrength();
  const displayStep = lockedAccountType && step === 3 ? 2 : step; // for the progress UI

  const stepLabel =
    step === 1
      ? 'Create your account'
      : step === 2 && !lockedAccountType
      ? 'Tell us about your business'
      : 'One last thing';

  const stepSubtitle =
    step === 1
      ? lockedAccountType === 'Enterprise'
        ? 'Sign up using the link your Zilch contact shared with you'
        : 'Just a few details to get started'
      : step === 2 && !lockedAccountType
      ? 'Pick the option that best describes what you do. You can change this later'
      : 'A couple of optional details, feel free to skip and add them later';

  return (
    <div className="signup-page">
      <div className="signup-shell">
        <div className="signup-brand">
          <img
            src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
            alt="Zilch"
            width={32}
            height={32}
          />
          <span>Zilch.</span>
        </div>

        <div className="signup-card">
          <div className="signup-progress" aria-hidden="true">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`signup-progress-dot${i + 1 <= displayStep ? ' is-active' : ''}`}
              />
            ))}
          </div>
          <div className="signup-step-meta">
            Step {displayStep} of {totalSteps}
          </div>

          <h1 className="signup-title">{stepLabel}</h1>
          <p className="signup-subtitle">{stepSubtitle}</p>

          {isSupplierClaim && step === 1 && (
            <div className="signup-banner">
              <strong>Claim your supplier workspace.</strong> Your products are already in
              Zilch and being used by distributors. Sign up with this email to take ownership.
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleStep1Submit} noValidate>
              <div className="signup-row">
                <div className="signup-field">
                  <label>Full Name</label>
                  <input
                    type="text"
                    className="signup-input"
                    placeholder="Jane Smith"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="signup-field">
                  <label>Business Name</label>
                  <input
                    type="text"
                    className="signup-input"
                    placeholder="Acme Promotional Products"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    autoComplete="organization"
                    required
                  />
                </div>
              </div>

              <div className="signup-field">
                <label>Email Address</label>
                <input
                  type="email"
                  className="signup-input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  readOnly={emailLocked}
                  title={emailLocked ? 'This is the email your claim link was sent to.' : undefined}
                  style={emailLocked ? { backgroundColor: '#f4f4f5', cursor: 'not-allowed' } : undefined}
                />
                {emailLocked && (
                  <p className="signup-hint">
                    Linked to your claim invitation — sign up with this email to take ownership of your products.
                  </p>
                )}
                {emailChanged && (
                  <p className="signup-hint signup-hint-warn">
                    Heads up — your products are linked to <strong>{claimEmail}</strong>. If
                    you sign up with a different email they won't be added to your account.
                  </p>
                )}
              </div>

              <div className="signup-row">
                <div className="signup-field">
                  <label>Password</label>
                  <input
                    type="password"
                    className="signup-input"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  {password && (
                    <p className="signup-hint" style={{ color: passwordStrength.color }}>
                      Strength: <strong>{passwordStrength.label}</strong>
                    </p>
                  )}
                </div>

                <div className="signup-field">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    className="signup-input"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <Turnstile
                key={captchaResetKey}
                action="signup"
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken('')}
              />

              {error && <div className="signup-error">{error}</div>}

              <button type="submit" className="signup-button" disabled={loading}>
                {loading ? 'Creating account…' : 'Create account'}
              </button>

              <p className="signup-legal">
                By continuing you agree to Zilch's{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms and Conditions
                </a>
                .
              </p>
            </form>
          )}

          {step === 2 && !lockedAccountType && (
            <div>
              <button
                type="button"
                className="signup-type-card"
                onClick={() => handleTypeSelect('Supplier')}
                disabled={loading}
              >
                <div className="signup-type-title">Supplier</div>
                <div className="signup-type-desc">
                  We manufacture or wholesale products that distributors resell.
                </div>
              </button>

              <button
                type="button"
                className="signup-type-card"
                onClick={() => handleTypeSelect('Distributor')}
                disabled={loading}
              >
                <div className="signup-type-title">Distributor</div>
                <div className="signup-type-desc">
                  We sell branded and unbranded promotional products to end customers.
                </div>
              </button>

              {error && <div className="signup-error">{error}</div>}
            </div>
          )}

          {step === 3 || (step === 2 && lockedAccountType) ? (
            <form onSubmit={handlePhoneSubmit} noValidate>
              <div className="signup-field">
                <label>Phone Number</label>
                <input
                  type="tel"
                  className="signup-input"
                  placeholder="+61 400 123 456"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                />
              </div>

              <div className="signup-field">
                <label>Warehouse Address</label>
                <AddressAutocomplete
                  value={warehouseAddress}
                  placeholder="Start typing your warehouse address…"
                  inputClassName="signup-input"
                  onSelect={({ address, lat, lng }) => {
                    setWarehouseAddress(address || '');
                    setWarehouseLat(lat ?? null);
                    setWarehouseLng(lng ?? null);
                  }}
                />
                <p className="signup-hint">If you don't have a warehouse, leave blank.</p>
              </div>

              {error && <div className="signup-error">{error}</div>}

              <button type="submit" className="signup-button" disabled={loading}>
                {loading ? 'Saving…' : (phone.trim() || warehouseAddress.trim()) ? 'Finish' : 'Skip for now'}
              </button>
            </form>
          ) : null}

          {step === 1 && (
            <p className="signup-foot">
              Already have an account?{' '}
              <Link to="/login">Sign in</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Signup;
